import React, { useEffect, useState } from "react";
import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import {
  MovieWrapper,
  ContextContainer,
  BoxContainer,
  StyledLabel,
  StyledSubLabel,
  LayoutWrapper,
  StyledTextFieldItem,
  StyledMUISelect,
} from "./styled";
import ContextMenu from "../ContextMenu";
import { ReactShakaPlayer } from "@mkhuda/react-shaka-player";
import Table from "../TableDetails";
import { useContentStore } from "../../stores/contentStore";
import {
  colorsCodes,
  convertToSeconds,
  toHHMMSS,
} from "../../utils/utilFunctions";
import TextFieldInput from "../Textfield";
import { locale } from "../../utils/LocaleManager";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { TagTypeEnum } from "../../enums/TagTypeEnum";
import TagsContextMenu from "../TagsContextMenu";
import Circle from "../Circle";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import { exportTranscript } from "../../services/contentServices";
import jsonToCsvExport from "json-to-csv-export";
import { toast } from "react-toastify";

let isPlaying = false;

const Layout = (props: any) => {
  const isShowDetails = useContentStore((state: any) => state.isShowDetails);
  const isShowEdit = useContentStore((state: any) => state.isShowEdit);
  const selectedCurrentTime = useContentStore(
    (state: any) => state.selectedCurrentTime,
  );
  const setSelectedTime = useContentStore(
    (state: any) => state.setSelectedTime,
  );
  const [mainPlayer, setMainPlayer] = useState<any>({});
  const isSelected = useContentStore((state: any) => state.isSelected);
  const [selectedTagType, setSelectedTagType] = useState(props.type);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [selectedFilter, setSelectedFilter] = useState<string[]>([]);
  const [searchKeyword, setSearchKeyword] = useState(props.searchKeyword);

  const onChangePlayer = (stats: any) => {
    if (stats) {
      const time = toHHMMSS(stats.mediaCurrentTime);
      setSelectedTime(time);
    }
    if (stats && isPlaying) {
      const scrollTime = toHHMMSS(stats.mediaCurrentTime);
      setSelectedTime(scrollTime);
      const offsetTop =
        document.getElementById(`st-row-${scrollTime}`)?.offsetTop ?? null;
      if (offsetTop) {
        document.getElementById("st-tabledetails")?.scrollTo({
          top: offsetTop - 57,
          behavior: "smooth",
        });
      }
    }
  };

  useEffect(() => {
    const { videoElement }: any = mainPlayer;

    if (!selectedCurrentTime || !videoElement) return;
    videoElement.currentTime = convertToSeconds(selectedCurrentTime);
    videoElement.pause();
  }, [mainPlayer, selectedCurrentTime]);

  useEffect(() => {
    const { player, videoElement }: any = mainPlayer;

    if (player && props.url) {
      async function play() {
        await player.load(props.url);
        videoElement.pause();
        videoElement.addEventListener("seeked", (event: any) => {
          if (event.isTrusted) {
            const scrollTime = toHHMMSS(videoElement.currentTime);
            const offsetTop =
              document.getElementById(`st-row-${scrollTime}`)?.offsetTop ??
              null;
            if (offsetTop) {
              document.getElementById("st-tabledetails")?.scrollTo({
                top: offsetTop - 57,
                behavior: "smooth",
              });
            }
          }
        });
      }
      play();
    }
  }, [props.url, mainPlayer]);

  const handleFilterChange = (
    event: SelectChangeEvent<typeof selectedFilter>,
  ) => {
    const {
      target: { value },
    } = event;
    setSelectedFilter(typeof value === "string" ? value.split(",") : value);
  };

  return (
    <LayoutWrapper>
      <Grid container spacing={1} alignContent={"center"}>
        <Grid item sm={6} md={6} lg={7} xl={7}>
          {isShowDetails ? (
            <Box
              component="section"
              sx={{
                background: "#F0F4F8",
                borderRadius: "0.5rem",
                height: "3.8125rem",
                padding: "0.5625rem 1rem",
              }}
            >
              <StyledLabel>{props.label}</StyledLabel>
              <StyledSubLabel style={{ fontSize: "1.5rem" }}>
                {props.title}
              </StyledSubLabel>
            </Box>
          ) : isShowEdit ? (
            <TextFieldInput
              variant="outlined"
              label="Video-Title"
              defaultValue={props.title}
              value={props.title}
              onChange={(e: any) => props.setTitle(e.target.value)}
            />
          ) : (
            <></>
          )}
          <MovieWrapper>
            <ReactShakaPlayer
              playerClassName="stplayer"
              className="stplayer"
              autoPlay={false}
              uiConfig={{ enableFullscreenOnRotation: false }}
              onPlayerError={props?.onPlayerError}
              onLoad={(player) => setMainPlayer(player)}
              onStatsChanged={(stats) => onChangePlayer(stats)}
              onPause={() => {
                isPlaying = false;
              }}
              onPlay={() => {
                isPlaying = true;
              }}
            />
          </MovieWrapper>
          <ContextContainer>
            <ContextMenu
              isShowPlayer={true}
              isShowDetails={isShowDetails}
              onCopyLinks={props.onCopyLinks}
              onDownload={props.onDownload}
              onClickEdit={props.onClickEdit}
              onClickDetails={props.onClickDetails}
              isShowEditDetails={isShowEdit}
              isShowEdit={props.isShowEdit}
              onClickRename={props.onClickRename}
            />
          </ContextContainer>
        </Grid>
        <Grid item sm={6} md={6} lg={5} xl={5}>
          <BoxContainer
            onMouseEnter={() => {
              isPlaying = false;
              mainPlayer.videoElement.pause();
            }}
          >
            <>
              <Grid container>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    gap: "0.625rem",
                    flexDirection: "column",
                    marginBottom: "1.5rem",
                  }}
                >
                  <StyledTextFieldItem>
                    <TextFieldInput
                      variant="outlined"
                      label={locale.search}
                      value={searchKeyword}
                      onChange={(e: any) => setSearchKeyword(e.target.value)}
                      InputProps={{
                        endAdornment: searchKeyword && (
                          <IconButton
                            size="small"
                            onClick={() => setSearchKeyword("")}
                          >
                            <ClearOutlinedIcon />
                          </IconButton>
                        ),
                      }}
                    />
                  </StyledTextFieldItem>
                  <StyledTextFieldItem>
                    <FormControl fullWidth>
                      <InputLabel id="select-type">{locale.filter}</InputLabel>
                      <StyledMUISelect
                        variant="outlined"
                        id="select"
                        labelId="select-label"
                        label={locale.filter}
                        multiple
                        value={selectedFilter}
                        //@ts-ignore
                        onChange={handleFilterChange}
                        renderValue={(selected: any) => (
                          <Box
                            sx={{
                              alignItems: "center",
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 1,
                            }}
                          >
                            {selected.map((value: any) => (
                              <>
                                <Circle
                                  key={value}
                                  toolTipTitle={value}
                                  color={colorsCodes(value)}
                                />
                                {value}
                              </>
                            ))}
                          </Box>
                        )}
                        endAdornment={
                          selectedFilter.length > 0 && (
                            <InputAdornment
                              position="end"
                              onClick={() => setSelectedFilter([])}
                              sx={{ position: "absolute", right: 32 }}
                            >
                              <IconButton size="small">
                                <ClearOutlinedIcon />
                              </IconButton>
                            </InputAdornment>
                          )
                        }
                        MenuProps={{
                          MenuListProps: {
                            sx: {
                              display: "flex",
                              flexDirection: "row",
                              flexWrap: "wrap",
                            },
                          },
                          disableScrollLock: true,
                        }}
                      >
                        {Object.values(TagTypeEnum)
                          .filter((type) => type !== TagTypeEnum.folder)
                          .map((type) => (
                            <MenuItem
                              key={type}
                              value={type}
                              sx={{
                                "&.Mui-selected": {
                                  backgroundColor: "transparent",
                                },
                                "&": {
                                  padding: 0,
                                  marginLeft: "6px",
                                  marginRight: "6px",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  backgroundColor: colorsCodes(type),
                                  padding: "10px 12px",
                                  borderRadius: "0.25rem",
                                  "&:hover": {
                                    filter: "brightness(85%)",
                                  },
                                }}
                              >
                                {selectedFilter.includes(type) ? (
                                  <CheckBoxOutlinedIcon />
                                ) : (
                                  <CheckBoxOutlineBlankIcon />
                                )}
                                {type}
                              </Box>
                            </MenuItem>
                          ))}
                      </StyledMUISelect>
                    </FormControl>
                  </StyledTextFieldItem>
                </Grid>
              </Grid>
              <Table
                tagDetails={props.tagDetails}
                searchKeyword={searchKeyword}
                selectedRow={selectedRow}
                onSelectedRow={(e: any) => {
                  setSelectedTagType(e.type);
                  setSelectedRow(e);
                  props.setSelectedRow(e);
                  props.setTagId(e.tagId);
                  props.setType(e.type);
                  props.setTag(e.name);
                  props.setTime(e.start.toString().substring(0, 8));
                }}
                setTag={props.setTag}
                setType={props.setType}
                selectedFilter={selectedFilter}
              />
            </>
          </BoxContainer>

          <ContextContainer>
            <TagsContextMenu
              isShowDetails={true}
              isRowSelected={selectedRow !== null}
              onClickEdit={() => {
                props.onClickEditTag();
              }}
              onClickExport={() => {
                (async () => {
                  const transcripts = await exportTranscript(props.videoId);
                  if (!transcripts?.data || transcripts.data.length === 0) {
                    toast.error(
                      locale.transcript_export_error,
                      {
                        position: "bottom-left",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: false,
                        pauseOnHover: false,
                        pauseOnFocusLoss: false,
                        draggable: true,
                        progress: undefined,
                        theme: "colored",
                      },
                    );
                    return;
                  }
                  jsonToCsvExport({
                    data: transcripts.data,
                    filename: `${props.title}.csv`,
                  });
                })();
              }}
            />
          </ContextContainer>
        </Grid>
      </Grid>
    </LayoutWrapper>
  );
};

export default Layout;
